/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useEffect, useState } from "react";
import { SYSTEM_COLOR } from "@adapptio/lib-components";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { Badge, BADGE_POSITION } from "./Badge";
import { DesignContext, RuntimeContext } from "@adapptio/lib-blueprint";
import { ActionModel } from "../../modules/ActionEditor/Common/ActionModel";
import { ViewModel } from "../../modules/ViewEditor/Common/ViewModel";
import { EndpointModel } from "../../modules/EndpointEditor/Common/EndpointModel";

export interface IEditorConsoleCountBadge {
	model: ViewModel | ActionModel | EndpointModel;
}

function getTotalErrorCounts(dCtx: DesignContext, rCtx: RuntimeContext) {
	const dErrors = dCtx.getErrorTypeCounts();
	const rErrors = rCtx.getErrorTypeCounts();

	return {
		error: rErrors.error + dErrors.error,
		warning: rErrors.warning + dErrors.warning,
		info: rErrors.info + dErrors.info,
		hint: rErrors.hint + dErrors.hint
	};
}

export const ConsoleCountBadge: FC<IEditorConsoleCountBadge> = ({ model }) => {
	const [ errorCount, setErrorCount ] = useState(() => getTotalErrorCounts(
		model.getDesignContext(),
		model.getRuntimeContext()
	));

	useEffect(() => {
		const handleErrors = () => setErrorCount(getTotalErrorCounts(
			model.getDesignContext(),
			model.getRuntimeContext()
		));

		onEvent(model.onParseErrors, handleErrors);
		onEvent(model.onRuntimeErrors, handleErrors);

		return () => {
			offEvent(model.onParseErrors, handleErrors);
			offEvent(model.onRuntimeErrors, handleErrors);
		}
	}, [ model ]);

	const sumSeverity = errorCount.error + errorCount.warning + errorCount.info + errorCount.hint;

	let color = SYSTEM_COLOR.INFO;

	if (errorCount.error > 0) {
		color = SYSTEM_COLOR.ERROR;
	} else if (errorCount.warning > 0) {
		color = SYSTEM_COLOR.WARNING;
	} else if (errorCount.hint > 0) {
		color = SYSTEM_COLOR.HINT;
	}

	return <Badge content={sumSeverity} color={color} position={BADGE_POSITION.END} />;
};
